/**
 * Ad landing page behaviour.
 *
 * The prototype kept a fake in-memory cart. This talks to the real Shopify AJAX
 * cart, so the threshold meter, the dock totals and the express checkout all
 * reflect what the shopper will actually be charged.
 *
 * Integration notes, learned the hard way on this theme:
 *  - Add via `cart/add.js`, then re-fetch `cart.js` and emit the theme's
 *    `cartUpdate` event. That is what keeps the drawer and the free-gift module
 *    in sync. Mirrors assets/product-attach-upsell.js.
 *  - `MinimogTheme.pubSubEvents.openCartDrawer` is a NO-OP on this theme. To open
 *    the drawer you must call `drawer.open()` directly.
 *  - The eligible subtotal EXCLUDES any line carrying the `_free_gift` property,
 *    matching snippets/cart-free-gift.liquid exactly. If the two ever disagree the
 *    shopper sees one number and Shopify applies another.
 */
(function () {
  var root = document.querySelector('[data-icl]');
  if (!root || root.dataset.iclReady === 'true') return;
  root.dataset.iclReady = 'true';

  var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var threshold = parseInt(root.dataset.threshold, 10);
  if (isNaN(threshold)) threshold = 7500;
  var moneyFormat = root.dataset.moneyFormat || '${{amount}}';

  var grid = root.querySelector('[data-icl-grid]');
  var strip = root.querySelector('[data-icl-pairs]');
  var dock = root.querySelector('[data-icl-dock]');
  var peek = root.querySelector('[data-icl-dock-open]');
  var peekLabel = root.querySelector('[data-icl-peek-label]');

  /* ---------- money ---------- */

  // Mirrors Shopify's own formatMoney so the dock agrees with the cart drawer.
  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    var amount = withCommas + '.' + parts[1];
    var noDecimals = withCommas;

    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals);
  }

  /* ---------- cart state ---------- */

  var inCart = {};   // product handle -> true

  function eligibleSubtotal(cart) {
    // Gift lines are excluded so the gift can never pay for itself and hold the
    // cart above the threshold. Same rule as cart-free-gift.liquid.
    return (cart.items || []).reduce(function (sum, item) {
      var props = item.properties || {};
      if (props._free_gift) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function paint(cart) {
    var subtotal = eligibleSubtotal(cart);
    var count = cart.item_count || 0;

    inCart = {};
    (cart.items || []).forEach(function (item) {
      if (item.handle) inCart[item.handle] = true;
    });

    var earned = subtotal >= threshold;
    dock.classList.toggle('is-earned', earned);
    dock.classList.toggle('has-items', count > 0);

    var msg = root.querySelector('[data-icl-meter-msg]');
    if (earned) {
      msg.innerHTML = root.dataset.msgEarned || 'Free shipping and your free charm are <b>unlocked</b>';
    } else {
      var remaining = threshold - subtotal;
      msg.innerHTML = (root.dataset.msgProgress || 'Add <b>%amount%</b> for free shipping and a free charm')
        .replace('%amount%', formatMoney(remaining));
    }

    root.querySelector('[data-icl-meter-amt]').textContent = formatMoney(subtotal);
    root.querySelector('[data-icl-fill]').style.width =
      Math.min(100, threshold ? (subtotal / threshold) * 100 : 0) + '%';

    var itemsEl = root.querySelector('[data-icl-items]');
    if (itemsEl) itemsEl.textContent = count + (count === 1 ? ' item' : ' items');
    var totalEl = root.querySelector('[data-icl-total]');
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price || 0);

    if (peekLabel) peekLabel.textContent = count + ' \u00b7 ' + formatMoney(cart.total_price || 0);
    syncDock();

    // Reflect membership on every card's button.
    root.querySelectorAll('[data-icl-add]').forEach(function (btn) {
      var card = btn.closest('[data-icl-card]');
      if (!card) return;
      var isIn = !!inCart[card.dataset.handle];
      btn.classList.toggle('is-in', isIn);
      btn.textContent = isIn ? (root.dataset.labelIn || 'In bag') : (root.dataset.labelAdd || 'Add');
    });
  }

  function refresh() {
    return fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        paint(cart);
        return cart;
      });
  }

  function addItems(items, button) {
    if (button) button.classList.add('is-busy');

    return fetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
        return refresh();
      })
      .then(function (cart) {
        // Keep the rest of the theme honest: the drawer, the free-gift module and
        // the header count all listen for this.
        if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
          MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, cart);
        }
        var drawer = document.querySelector('m-cart-drawer');
        if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
          drawer.onCartDrawerUpdate();
          drawer.classList.toggle('m-cart--empty', cart.item_count === 0);
        }
        return cart;
      })
      .catch(function (error) {
        // Surface it rather than failing silently - a dead Add button is worse
        // than an explained one.
        var note = root.querySelector('[data-icl-error]');
        if (note) {
          note.textContent = error.message || 'Something went wrong. Please try again.';
          note.hidden = false;
          window.setTimeout(function () { note.hidden = true; }, 6000);
        }
      })
      .finally(function () {
        if (button) button.classList.remove('is-busy');
      });
  }

  /* ---------- variant resolution ---------- */

  function chosenMetal(card) {
    var pressed = card.querySelector('[data-icl-metal][aria-pressed="true"]');
    return pressed ? pressed.dataset.metal : card.dataset.defaultMetal || '';
  }

  function variantFor(card, metal) {
    var variants;
    try {
      variants = JSON.parse(card.dataset.variants || '[]');
    } catch (e) {
      return null;
    }
    if (!metal) return variants.find(function (v) { return v.available; }) || variants[0] || null;
    var match = variants.filter(function (v) {
      return String(v.metal).toLowerCase() === String(metal).toLowerCase();
    });
    return match.find(function (v) { return v.available; }) || match[0] || null;
  }

  /* ---------- the pairs-with prompt ---------- */

  function closePairs() {
    if (strip) strip.classList.remove('is-open');
  }

  function columnCount() {
    return getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length || 2;
  }

  function openPairs(card) {
    if (!strip) return;

    var partnerHandle = card.dataset.pair;
    if (!partnerHandle) return closePairs();

    var partner = grid.querySelector('[data-icl-card][data-handle="' + partnerHandle + '"]');
    if (!partner) return closePairs();
    if (inCart[partnerHandle]) return closePairs();

    // Metal match or nothing. Offering a metal the shopper did not choose is how
    // you lose the sale, and it is what the PDP attach-upsell already does.
    var metal = chosenMetal(card);
    var variant = variantFor(partner, metal);
    if (!variant || !variant.available) return closePairs();

    // Move the strip to sit after the last card in this product's row. Hard-coding
    // a position bakes in a column count and breaks the four-up desktop grid.
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-icl-card]'));
    var index = cards.indexOf(card);
    var cols = columnCount();
    var lastInRow = Math.min((Math.floor(index / cols) + 1) * cols - 1, cards.length - 1);
    var anchor = cards[lastInRow];
    if (anchor && anchor.nextSibling !== strip) grid.insertBefore(strip, anchor.nextSibling);

    strip.querySelector('[data-icl-pairs-img]').src = partner.dataset.image || '';
    strip.querySelector('[data-icl-pairs-img]').alt = partner.dataset.title || '';
    strip.querySelector('[data-icl-pairs-name]').textContent =
      partner.dataset.title + ' (' + metal + ') ' + formatMoney(variant.price);

    var addBtn = strip.querySelector('[data-icl-pairs-add]');
    addBtn.dataset.variantId = variant.id;
    strip.classList.add('is-open');
  }

  /* ---------- metal -> photo ---------- */

  // The card is rendered with image_tag, which emits a srcset. Setting src alone is
  // not enough: with a srcset present the browser keeps resolving from it and the
  // photo never visibly changes. So the srcset has to be rebuilt (or dropped) too.
  function showMetalImage(card) {
    var img = card.querySelector('.icl-shot');
    if (!img) return;

    // Stash what the server rendered, once, so a variant with no image of its own
    // can put the product featured shot back rather than keeping the wrong metal.
    if (img.dataset.iclBaseSrc === undefined) {
      img.dataset.iclBaseSrc = img.getAttribute('src') || '';
      img.dataset.iclBaseSrcset = img.getAttribute('srcset') || '';
    }

    var variant = variantFor(card, chosenMetal(card));
    var url = variant && variant.image;

    if (!url) {
      if (img.dataset.iclBaseSrc) img.setAttribute('src', img.dataset.iclBaseSrc);
      if (img.dataset.iclBaseSrcset) img.setAttribute('srcset', img.dataset.iclBaseSrcset);
      else img.removeAttribute('srcset');
      return;
    }

    // Liquid emitted this at width=700. Shopify's CDN takes any width, so the
    // responsive set can be rebuilt from the one URL. If the shape is ever not what
    // we expect, drop srcset entirely - a correct photo at one size beats a stale
    // photo at three.
    if (/([?&])width=700(&|$)/.test(url)) {
      img.setAttribute('srcset', [300, 500, 700].map(function (w) {
        return url.replace(/([?&])width=700(&|$)/, '$1width=' + w + '$2') + ' ' + w + 'w';
      }).join(', '));
    } else {
      img.removeAttribute('srcset');
    }
    img.setAttribute('src', url);
  }

  // Variant images are assigned per product in Shopify and not every product has
  // them, so seed each card from its own current selection on load.
  grid.querySelectorAll('[data-icl-card]').forEach(showMetalImage);

  /* ---------- events ---------- */

  grid.addEventListener('click', function (event) {
    var metalBtn = event.target.closest('[data-icl-metal]');
    if (metalBtn) {
      var mCard = metalBtn.closest('[data-icl-card]');
      mCard.querySelectorAll('[data-icl-metal]').forEach(function (b) {
        b.setAttribute('aria-pressed', 'false');
      });
      metalBtn.setAttribute('aria-pressed', 'true');
      showMetalImage(mCard);
      return;
    }

    var addBtn = event.target.closest('[data-icl-add]');
    if (addBtn) {
      var card = addBtn.closest('[data-icl-card]');
      var variant = variantFor(card, chosenMetal(card));
      if (!variant || !variant.available) return;
      addItems([{ id: variant.id, quantity: 1, properties: { _icl: 'landing' } }], addBtn)
        .then(function () { openPairs(card); });
      return;
    }

    var pairBtn = event.target.closest('[data-icl-pairs-add]');
    if (pairBtn && pairBtn.dataset.variantId) {
      addItems(
        [{ id: Number(pairBtn.dataset.variantId), quantity: 1, properties: { _icl: 'landing-pair' } }],
        pairBtn
      ).then(closePairs);
    }
  });

  var bundleBtn = root.querySelector('[data-icl-bundle-add]');
  if (bundleBtn) {
    bundleBtn.addEventListener('click', function () {
      var handles = (bundleBtn.dataset.handles || '').split(',').filter(Boolean);
      var items = [];
      handles.forEach(function (handle) {
        var card = grid.querySelector('[data-icl-card][data-handle="' + handle + '"]');
        if (!card || inCart[handle]) return;
        var variant = variantFor(card, chosenMetal(card));
        if (variant && variant.available) {
          items.push({ id: variant.id, quantity: 1, properties: { _icl: 'landing-bundle' } });
        }
      });
      if (items.length) addItems(items, bundleBtn);
    });
  }

  var checkoutAll = root.querySelector('[data-icl-checkout]');
  if (checkoutAll) {
    checkoutAll.addEventListener('click', function () {
      var drawer = document.querySelector('m-cart-drawer');
      // The pub/sub open event is a no-op on this theme; call open() directly.
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = rootUrl + 'cart';
    });
  }

  // Another surface (drawer, quantity edit, gift module) changed the cart.
  if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, function (cart) {
      if (cart && cart.items) paint(cart);
      else refresh();
    });
  }

  /* ---------- dismissing the dock ----------
     Remembered for the session so it does not reappear on every page view, but
     deliberately not permanent: this bar is the only checkout path on the page,
     so dismissing it has to leave a way back. */
  var DOCK_KEY = 'iclDockDismissed';

  function dockDismissed() {
    try { return sessionStorage.getItem(DOCK_KEY) === '1'; } catch (e) { return false; }
  }

  function rememberDock(dismissed) {
    try { sessionStorage.setItem(DOCK_KEY, dismissed ? '1' : '0'); } catch (e) {}
  }

  function syncDock() {
    var hidden = dockDismissed();
    dock.hidden = hidden;
    // Only offer the way back when there is a cart to get back to.
    if (peek) peek.hidden = !(hidden && dock.classList.contains('has-items'));
    // Releases the theme's scroll-to-top button; see the rule in ic-landing.css.
    document.documentElement.classList.toggle('icl-dock-open', !hidden);
    positionDock();
  }

  var dockCloseBtn = dock.querySelector('[data-icl-dock-close]');
  if (dockCloseBtn) {
    dockCloseBtn.addEventListener('click', function () { rememberDock(true); syncDock(); });
  }
  if (peek) {
    peek.addEventListener('click', function () { rememberDock(false); syncDock(); });
  }

  /* ---------- keep clear of the theme's own fixed mobile bar ---------- */

  // Minimog renders `.m-mobile-sticky-bar` fixed to the bottom on small screens and
  // hides it further up. Measuring it beats hard-coding a breakpoint, which would
  // silently drift if the theme's md value ever changes.
  function positionDock() {
    var bar = document.querySelector('.m-mobile-sticky-bar');
    var barHeight = 0;
    if (bar) {
      var cs = getComputedStyle(bar);
      if (cs.display !== 'none' && cs.visibility !== 'hidden' && cs.position === 'fixed') {
        barHeight = bar.getBoundingClientRect().height;
      }
    }

    if (barHeight > 0) dock.style.bottom = barHeight + 'px';
    else dock.style.removeProperty('bottom'); // let the stylesheet's desktop rule win

    // The peek pill stands in for the dock when dismissed, so it needs the same lift.
    var peekHeight = 0;
    if (peek && !peek.hidden) {
      peek.style.bottom = (barHeight + 16) + 'px';
      peekHeight = peek.getBoundingClientRect().height + 16;
    }

    // Reserve exactly enough room that the closer is never trapped behind either bar.
    var dockHeight = dock.hidden ? 0 : dock.getBoundingClientRect().height;
    root.style.paddingBottom = Math.ceil(Math.max(dockHeight, peekHeight) + barHeight + 28) + 'px';

    // Gorgias parks its launcher bottom-right, which on desktop is the same corner
    // the dock occupies. Its z-index is lowered in the stylesheet so the dock wins
    // any overlap, but on desktop that would bury the launcher completely, so lift
    // it over the card instead. Below 900px the dock is full width and the
    // stylesheet's 71px rule handles it.
    var chat = document.getElementById('chat-button');
    if (chat) {
      if (window.matchMedia('(min-width: 900px)').matches) {
        // 24px is the dock's own offset from the bottom on desktop, plus a 12px gap.
        chat.style.setProperty('bottom', (dock.hidden ? 20 : Math.ceil(dockHeight + 36)) + 'px', 'important');
      } else {
        chat.style.removeProperty('bottom');
      }
    }
  }

  // Column count may have changed, which makes the strip's row anchor stale.
  var lastCols = null;
  window.addEventListener('resize', function () {
    var cols = columnCount();
    if (lastCols !== null && cols !== lastCols) closePairs();
    lastCols = cols;
    positionDock();
  });

  // The dock grows when the checkout block appears, so re-measure after any paint.
  if (window.ResizeObserver) new ResizeObserver(positionDock).observe(dock);

  // Gorgias injects its launcher asynchronously, so the first positionDock() runs
  // before #chat-button exists and the desktop lift above is a no-op. Re-run once
  // it appears, then stop watching.
  if (!document.getElementById('chat-button') && window.MutationObserver) {
    var chatWatch = new MutationObserver(function () {
      if (!document.getElementById('chat-button')) return;
      chatWatch.disconnect();
      positionDock();
    });
    chatWatch.observe(document.body, { childList: true, subtree: true });
    // Belt and braces: stop watching after 15s even if it never loads.
    setTimeout(function () { chatWatch.disconnect(); }, 15000);
  }

  syncDock();
  refresh();
})();
